import { useCallback, useEffect, useMemo, useRef } from 'react';
import { createUseStyles } from 'react-jss';

import { Colors, Theme } from '@app/theme';
import {
  Button,
  ButtonVariant,
  CenteredLoader,
  CenteredModal,
  DisplayQrCode,
  useBoolModalState,
  useLinkToMeeting,
} from '@app/core';
import {
  DevicesCategory,
  ModeratedTaskFormat,
  Participation,
  SessionQuota,
  StudyType,
  useHaveQuota,
  useMe,
  usePublicSession,
  useTaskMeeting,
} from '@app/api';
import { useLoginParticipantWithModal } from '@app/auth';
import { useTranslation } from 'react-i18next';
import { useBrowserDeviceCategory } from '../Home/components/useBrowserDeviceCategory';
import { getLocale, i18n } from '@app/i18n';
import classnames from 'classnames';
import { useGenerateToMobileQrcodeUrl } from './useGenerateToMobileQrcodeUrl';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.marginBase,
  },
  descriptionModal: {
    ...theme.fonts.caption,
  },
  descriptionTitle: {
    paddingTop: theme.marginBase * 2,
    paddingBottom: theme.marginBase * 2,
    textAlign: 'center',
    ...theme.fonts.h5,
  },
  noLinkBtn: {
    opacity: 0.5,
    justifyContent: 'center',
  },
  button: {
    justifyContent: 'center',
  },
  icons: {
    fontSize: theme.iconSize.large,
    paddingLeft: theme.marginBase,
    alignSelf: 'center',
    fontWeight: 500,
  },
}));

interface Props {
  participation: Participation;
  className?: string;
  color?: Colors;
  variant?: ButtonVariant;
  big?: boolean;
  disabled?: boolean;
  fallbackDeviceModal?: boolean;
}

const EMPTY_ARRAY: DevicesCategory[] = [];

export const LinkToMeetingButton = ({
  participation,
  className,
  color,
  variant,
  big,
  disabled,
  fallbackDeviceModal,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { data: me } = useMe(true);
  const { data: session } = usePublicSession(participation.sessionId);
  const isUnModerated = session?.studyType === StudyType.UNMODERATED;
  const isModerated = session?.studyType === StudyType.MODERATED;
  const requireLogin = !!participation.participantId && !!isUnModerated;
  const haveDeviceQuota = useHaveQuota(
    session?.id,
    SessionQuota.ALLOWED_DEVICES_CATEGORY,
  );

  const { data: meeting } = useTaskMeeting(
    !session || isUnModerated ? undefined : participation.taskId,
  );
  const [qrCodeUrl, generateQrCodeUrl] = useGenerateToMobileQrcodeUrl();

  const openLoginModal = useLoginParticipantWithModal();

  const [deviceModalOpen, openDeviceModal, closeDeviceModal] =
    useBoolModalState(fallbackDeviceModal);

  const deviceType = useBrowserDeviceCategory();

  const linkToMeetingExist = useMemo(() => {
    if (meeting?.format === ModeratedTaskFormat.VIDEO_CALL) {
      return meeting.videoCallLink;
    }
    // @TODO get from task Settings
    return true;
  }, [meeting, session]);

  const startLink = useLinkToMeeting(participation.id);
  const linkToMeeting = linkToMeetingExist ? startLink : undefined;

  const availableDevices = isUnModerated
    ? session?.availableDevicesCategories || EMPTY_ARRAY
    : session?.allowedDevicesCategories || EMPTY_ARRAY;

  const shouldOpenDeviceModal = useMemo(() => {
    if (!availableDevices || availableDevices.length === 0) {
      return false;
    }

    let hasValidDevice = !!availableDevices?.includes(deviceType);
    if (isModerated && haveDeviceQuota) {
      let onSelectedDevice = true;

      if (participation?.selectedDevice) {
        onSelectedDevice = participation?.selectedDevice === deviceType;
      }

      hasValidDevice = hasValidDevice && onSelectedDevice;
    }

    return !hasValidDevice;
  }, [
    availableDevices,
    deviceType,
    haveDeviceQuota,
    isModerated,
    participation?.selectedDevice,
  ]);

  const myDeviceString: Record<DevicesCategory, string> = {
    [DevicesCategory.COMPUTER]: t('home.modal.notGoodDevice.computer'),
    [DevicesCategory.TABLET]: t('home.modal.notGoodDevice.tablet'),
    [DevicesCategory.SMARTPHONE]: t('home.modal.notGoodDevice.smartphone'),
  };

  const deviceNeededString = useMemo(() => {
    const formatter = new Intl.ListFormat(getLocale(i18n), {
      style: 'long',
      type: 'disjunction',
    });

    if (isModerated && haveDeviceQuota && participation?.selectedDevice) {
      return t(myDeviceString[participation.selectedDevice]);
    }

    return formatter.format(
      availableDevices.map((device) => myDeviceString[device]),
    );
  }, [
    availableDevices,
    haveDeviceQuota,
    isModerated,
    myDeviceString,
    participation.selectedDevice,
    t,
  ]);

  const timerRef = useRef<NodeJS.Timeout>();

  const onClick = useCallback(async () => {
    try {
      if (!me && requireLogin && participation.participantId) {
        await openLoginModal(participation.participantId);
        return;
      }
    } catch (e) {
      return;
    }

    if (shouldOpenDeviceModal) {
      if (deviceType === DevicesCategory.COMPUTER) {
        await generateQrCodeUrl();
        const delay = 1000 * 5 * 60;
        timerRef.current = setTimeout(() => {
          closeDeviceModal();
        }, delay);
      }
      openDeviceModal();
      return;
    }
  }, [
    shouldOpenDeviceModal,
    me,
    requireLogin,
    participation.participantId,
    openLoginModal,
    deviceType,
    openDeviceModal,
    generateQrCodeUrl,
    closeDeviceModal,
  ]);

  const generateQrCode = useCallback(
    () => generateQrCodeUrl(),
    [generateQrCodeUrl],
  );
  useEffect(() => {
    if (deviceModalOpen && deviceType === DevicesCategory.COMPUTER) {
      generateQrCode();
    }
  }, [deviceModalOpen, deviceType, generateQrCode]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  if (!session) {
    return <CenteredLoader />;
  }

  return (
    <>
      <Button
        color={color || 'green'}
        variant={variant || 'plain'}
        className={classnames(
          !linkToMeeting ? classes.noLinkBtn : classes.button,
          className,
        )}
        small={!big}
        href={
          !shouldOpenDeviceModal && (me || !requireLogin) && !disabled
            ? linkToMeeting
            : undefined
        }
        rel="noopener noreferrer"
        disabled={!linkToMeeting || disabled}
        target="_blank"
        onClick={onClick}
      >
        {isUnModerated
          ? t('booked.summary.joinStudyLink')
          : t('home.nextMeeting.joinMeeting')}
        <i className={classnames('ri-share-box-line', classes.icons)} />
      </Button>
      <CenteredModal
        isOpen={deviceModalOpen}
        title={t('home.modal.notGoodDevice.title')}
        onRequestClose={closeDeviceModal}
        buttons={[
          session.studyType === StudyType.MODERATED && !haveDeviceQuota && (
            <Button
              small={true}
              href={`${linkToMeeting}?bypassDeviceCheck=true`}
              rel="noopener noreferrer"
              target="_blank"
              key="button-modal-booked-participant-1"
            >
              {t('home.modal.notGoodDevice.button')}
            </Button>
          ),
        ]}
      >
        <p className={classes.descriptionModal}>
          {t(`home.modal.notGoodDevice.description.${session.studyType}`, {
            deviceNeeded: deviceNeededString,
            currentDevice: myDeviceString[deviceType],
          })}
        </p>
        {qrCodeUrl && (
          <>
            <p className={classes.descriptionTitle}>
              {t(`auth.login.action.studyStart.qrCode.title`)}
            </p>
            <DisplayQrCode link={qrCodeUrl} />
          </>
        )}
      </CenteredModal>
    </>
  );
};
